// Settings › Agents and tokens › Agent handoff (plan step 4.3; D44, D96; APS-310): owners connect
// GitHub (the Apsio App, on the repositories they choose) or Linear (the Apsio app, acting as
// itself), choose where issues go, and turn it on, typing where to confirm. Until then nothing is
// sent. Admins turn it off; members see what was handed over, and what came of it: the pull
// request that references the GitHub issue. Apsio never pushes code, merges or assigns.

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useId, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import {
  handoffConnectionQuery,
  useConnectHandoff,
  useCreateHandoffDestination,
  useSetHandoff,
} from '../api/queries'
import type { AlertDestination, Handoff, HandoffPullRequest } from '../api/types'
import { Btn, HovRow, Seg, Status, type StatusKey, Table } from '../components/d'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'

export const isHandoff = (d: Pick<AlertDestination, 'kind'>) =>
  d.kind === 'github' || d.kind === 'linear'

/** Connect GitHub or Linear: the owner leaves for the provider and comes back here. */
export function HandoffConnect({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useI18n()
  const connect = useConnectHandoff(orgId)
  const go = (provider: 'github' | 'linear') =>
    connect.mutate(
      { provider, project_id: projectId },
      { onSuccess: ({ url }) => window.location.assign(url) },
    )
  return (
    <div className="se-sp" style={{ paddingTop: 12 }}>
      <span className="se-who">
        <Btn variant="ghost" size="sm" disabled={connect.isPending} onClick={() => go('github')}>
          {t('settings.handoff.connectGitHub')}
        </Btn>
        <Btn variant="ghost" size="sm" disabled={connect.isPending} onClick={() => go('linear')}>
          {t('settings.handoff.connectLinear')}
        </Btn>
      </span>
      {connect.error ? (
        <p role="alert" className="notice error">
          {isApiError(connect.error, 503)
            ? t('settings.handoff.notSetUp')
            : isApiError(connect.error, 403)
              ? t('settings.handoff.ownersOnly')
              : t('state.failed')}
        </p>
      ) : null}
    </div>
  )
}

/** Back from GitHub or Linear: choose the repository or the team; it is created off. */
export function HandoffChoose({ orgId, connectionId }: { orgId: string; connectionId: string }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const connection = useQuery(handoffConnectionQuery(orgId, connectionId))
  const create = useCreateHandoffDestination(orgId)
  const [choice, setChoice] = useState('')
  const [name, setName] = useState('')
  const [cap, setCap] = useState('5')
  const ids = { choice: useId(), name: useId(), cap: useId() }
  const done = () =>
    navigate({
      to: '.',
      search: (s: Record<string, unknown>) => ({ ...s, handoff: undefined }),
    } as never)
  if (connection.isPending) return <Loading />
  if (connection.isError)
    return (
      <div>
        {isApiError(connection.error, 404) ? (
          <p role="alert" className="notice error">
            {t('settings.handoff.connectionGone')}
          </p>
        ) : (
          <ErrorState error={connection.error} retry={() => connection.refetch()} />
        )}
      </div>
    )
  const c = connection.data
  const options =
    c.provider === 'github'
      ? (c.repositories ?? []).map((r) => ({ value: String(r.id), label: `${r.owner}/${r.name}` }))
      : (c.teams ?? []).map((team) => ({ value: team.id, label: `${team.key} · ${team.name}` }))
  const selected = choice || options[0]?.value || ''
  const capValue = Number(cap)
  const ready = selected !== '' && name.trim() !== '' && capValue >= 1 && capValue <= 50
  return (
    <form
      className="se-form"
      aria-label={t('settings.handoff.choose')}
      onSubmit={(e) => {
        e.preventDefault()
        if (!ready) return
        create.mutate(
          {
            connection_id: connectionId,
            name: name.trim(),
            ...(c.provider === 'github'
              ? { repository_id: Number(selected) }
              : { team_id: selected }),
            daily_cap: capValue,
          },
          { onSuccess: done },
        )
      }}
    >
      <p role="status" className="notice ok">
        {t(
          c.provider === 'github'
            ? 'settings.handoff.connectedGitHub'
            : 'settings.handoff.connectedLinear',
          { account: c.account },
        )}
      </p>
      <div className="form-row">
        <div className="field">
          <label htmlFor={ids.choice}>
            {t(c.provider === 'github' ? 'settings.handoff.repository' : 'settings.handoff.team')}
          </label>
          <select
            id={ids.choice}
            className="select"
            value={selected}
            onChange={(e) => setChoice(e.target.value)}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={ids.name}>{t('settings.alerts.name')}</label>
          <input
            id={ids.name}
            className="input"
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={ids.cap}>{t('settings.handoff.dailyCap')}</label>
          <input
            id={ids.cap}
            className="input"
            type="number"
            min={1}
            max={50}
            step={1}
            value={cap}
            onChange={(e) => setCap(e.target.value)}
          />
        </div>
      </div>
      {options.length === 0 ? (
        <p className="notice">{t('settings.handoff.nothingToChoose')}</p>
      ) : null}
      <div className="form-row">
        <button type="submit" className="btn small" disabled={!ready || create.isPending}>
          {t('settings.handoff.create')}
        </button>
        <button type="button" className="btn small" onClick={done}>
          {t('settings.cancel')}
        </button>
      </div>
      {create.error ? (
        <p role="alert" className="notice error">
          {isApiError(create.error, 402)
            ? t('settings.alerts.tooManyDestinations')
            : isApiError(create.error, 409) || isApiError(create.error, 404)
              ? t('settings.handoff.connectionGone')
              : t('state.failed')}
        </p>
      ) : null}
    </form>
  )
}

/** On, off or revoked, for a handoff destination: a status, the glyph and a word. */
export function HandoffStatus({ destination: d }: { destination: AlertDestination }) {
  const { t } = useI18n()
  if (d.revoked_at) return <Status s="miss" label={t('settings.handoff.revoked')} />
  if (d.handoff_enabled_at) return <Status s="on" label={t('settings.handoff.onWord')} />
  return <Status s="mute" label={t('settings.handoff.offWord')} />
}

/**
 * Off or On (Console D's seg): On asks an owner, in a dialog that says what will happen, to type
 * where issues go; Off stops it at once (owners and admins). Who may change neither sees the
 * status; a revoked destination says so.
 */
export function HandoffSwitch({
  orgId,
  destination: d,
  role,
}: {
  orgId: string
  destination: AlertDestination
  role: 'owner' | 'admin' | 'member' | undefined
}) {
  const { t } = useI18n()
  const set = useSetHandoff(orgId)
  const ref = useRef<HTMLDialogElement>(null)
  const [typed, setTyped] = useState('')
  const ids = { title: useId(), input: useId() }
  const on = Boolean(d.handoff_enabled_at)
  const owner = role === 'owner'
  const mayChange = on ? owner || role === 'admin' : owner
  if (d.revoked_at || !mayChange) return <HandoffStatus destination={d} />
  return (
    <>
      <Seg
        label={t('settings.handoff.switch', { where: d.host })}
        options={[
          { value: 'off', label: t('settings.handoff.off') },
          { value: 'on', label: t('settings.handoff.on') },
        ]}
        value={on ? 'on' : 'off'}
        onChange={(v) => {
          if (set.isPending || (v === 'on') === on) return
          if (v === 'off') set.mutate({ destinationId: d.id, enabled: false })
          else {
            setTyped('')
            set.reset()
            ref.current?.showModal()
          }
        }}
      />
      {set.error && !ref.current?.open ? (
        <span role="alert" className="st miss">
          {t('state.failed')}
        </span>
      ) : null}
      <dialog ref={ref} aria-labelledby={ids.title}>
        <form
          className="stack"
          style={{ gap: 14 }}
          onSubmit={(e) => {
            e.preventDefault()
            if (typed.trim() !== d.host) return
            set.mutate(
              { destinationId: d.id, enabled: true, confirm: typed.trim() },
              { onSuccess: () => ref.current?.close() },
            )
          }}
        >
          <h2 id={ids.title} style={{ fontSize: 17 }}>
            {t('settings.handoff.turnOnTitle')}
          </h2>
          <p className="dim">
            {t('settings.handoff.turnOnBody', { where: d.host, n: d.daily_cap ?? 5 })}
          </p>
          <div className="field">
            <label htmlFor={ids.input}>
              {t('settings.handoff.typeToConfirm', { where: d.host })}
            </label>
            <input
              id={ids.input}
              className="input mono"
              translate="no"
              autoComplete="off"
              spellCheck={false}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </div>
          {set.error ? (
            <p role="alert" className="notice error">
              {isApiError(set.error, 409)
                ? t('settings.handoff.revoked')
                : isApiError(set.error, 403)
                  ? t('settings.handoff.noAccess')
                  : t('state.failed')}
            </p>
          ) : null}
          <div className="head-actions">
            <button
              type="submit"
              className="btn solid"
              disabled={typed.trim() !== d.host || set.isPending}
            >
              {t('settings.handoff.turnOn')}
            </button>
            <button type="button" className="btn" onClick={() => ref.current?.close()}>
              {t('settings.cancel')}
            </button>
          </div>
        </form>
      </dialog>
    </>
  )
}

/** Where a destination sends issues, as people name it: `acme/shop-ios`, `linear.app/ENG`. */
export const handoffWhere = (d: Pick<AlertDestination, 'kind' | 'host'>) =>
  d.kind === 'github' ? d.host.replace(/^github\.com\//, '') : d.host

const safeUrl = (url: string | null | undefined) =>
  url && /^https:\/\//.test(url) ? url : undefined

/** What came of a handoff: its pull request on GitHub, else the issue's own state. */
export function HandoffThen({ handoff: h }: { handoff: Handoff }) {
  const { t } = useI18n()
  const pr = h.pull_request
  if (pr) {
    const s: Record<HandoffPullRequest['state'], StatusKey> = {
      in_review: 'reg',
      draft: 'on',
      merged: 'res',
      closed: 'mute',
    }
    const label = t(PR_LABEL[pr.state], { n: pr.number })
    const url = safeUrl(pr.url)
    return url ? (
      <a className="ul" href={url} target="_blank" rel="noreferrer noopener">
        <Status s={s[pr.state]} label={label} />
      </a>
    ) : (
      <Status s={s[pr.state]} label={label} />
    )
  }
  if (h.state === 'failed')
    return (
      <>
        <Status s="miss" label={t('settings.handoff.state.failed')} />{' '}
        {h.error_code ? (
          <span className="faint mono" translate="no">
            {h.error_code}
          </span>
        ) : null}
      </>
    )
  if (h.state === 'claimed') return <Status s="on" label={t('settings.handoff.state.claimed')} />
  return h.provider === 'github' ? (
    <Status s="on" label={t('settings.handoff.noPullRequest')} />
  ) : (
    <Status s="res" label={t('settings.handoff.state.opened')} />
  )
}

const PR_LABEL = {
  in_review: 'settings.handoff.pr.in_review',
  draft: 'settings.handoff.pr.draft',
  merged: 'settings.handoff.pr.merged',
  closed: 'settings.handoff.pr.closed',
} as const satisfies Record<HandoffPullRequest['state'], MessageId>

const STATE: Record<Handoff['state'], { s: StatusKey; label: MessageId }> = {
  opened: { s: 'res', label: 'settings.handoff.state.opened' },
  claimed: { s: 'on', label: 'settings.handoff.state.claimed' },
  failed: { s: 'miss', label: 'settings.handoff.state.failed' },
}

/**
 * The issues this project handed over, newest first: the crash group, the GitHub or Linear issue,
 * and what came of it (the pull request). Settings adds the issue's own state.
 */
export function HandoffsTable({
  orgId,
  projectId,
  handoffs,
  destinations,
  withState,
}: {
  orgId: string
  projectId: string
  handoffs: Handoff[]
  destinations: AlertDestination[]
  withState?: boolean
}) {
  const { t, relative } = useI18n()
  const navigate = useNavigate()
  const where = new Map(destinations.map((d) => [d.id, handoffWhere(d)]))
  return (
    <Table label={t('settings.handoff.list')}>
      <thead>
        <tr>
          <th className="f">{t('settings.handoff.col.group')}</th>
          <th>{t('settings.handoff.col.issue')}</th>
          {withState ? <th>{t('settings.handoff.col.state')}</th> : null}
          <th>{t('settings.handoff.col.then')}</th>
          <th className="num last">{t('settings.handoff.when')}</th>
        </tr>
      </thead>
      <tbody>
        {handoffs.map((h) => {
          const url = safeUrl(h.url)
          const repo = h.provider === 'github' ? where.get(h.destination_id) : undefined
          const ref = h.external_ref ? `${repo ?? ''}${h.external_ref}` : undefined
          const cells = (
            <>
              <td className="f wrap">
                {/* The row opens the issue (HovRow), as the mock's: the title is text. */}
                <span className="ttl" translate="no">
                  {h.subject}
                </span>
                {h.issue_id ? (
                  <span className="t2" translate="no">
                    {h.issue_id}
                  </span>
                ) : null}
              </td>
              <td translate="no">
                {url && ref ? (
                  <a className="ul" href={url} target="_blank" rel="noreferrer noopener">
                    {ref}
                  </a>
                ) : (
                  (ref ?? '—')
                )}
              </td>
              {withState ? (
                <td>
                  <Status s={STATE[h.state].s} label={t(STATE[h.state].label)} />
                </td>
              ) : null}
              <td>
                <HandoffThen handoff={h} />
              </td>
              <td className="num last">{relative(h.created_at)}</td>
            </>
          )
          const issueId = h.issue_id
          return issueId ? (
            <HovRow
              key={h.id}
              onOpen={() =>
                navigate({
                  to: '/o/$orgId/p/$projectId/issues/$issueId',
                  params: { orgId, projectId, issueId },
                })
              }
            >
              {cells}
            </HovRow>
          ) : (
            <tr key={h.id}>{cells}</tr>
          )
        })}
      </tbody>
    </Table>
  )
}
